import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const ACCOUNT_COLORS = ["#1e3a5f", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444", "#3b82f6", "#ec4899", "#06b6d4"];

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const fmtFull = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const CHANGE_LABELS = {
  account_created: "Created",
  manual_update: "Updated",
  manual_snapshot: "Snapshot",
  quick_transfer_in: "Transfer In",
  quick_transfer_out: "Transfer Out",
  allocation_complete: "Allocation",
};

const BalanceHistory = ({ businessId }) => {
  const [historyData, setHistoryData] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [loading, setLoading] = useState(true);
  const [snapshotting, setSnapshotting] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await api.get(`/balance-history/business/${businessId}`);
      setHistoryData(res.data);
    } catch (error) {
      console.error("Failed to fetch balance history:", error);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSnapshot = async () => {
    setSnapshotting(true);
    try {
      await api.post(`/balance-history/snapshot/${businessId}`);
      await fetchHistory();
    } catch (error) {
      console.error("Snapshot failed:", error);
    } finally {
      setSnapshotting(false);
    }
  };

  // Build chart data
  const buildChartData = () => {
    if (!historyData.length) return [];

    if (selectedAccount === "all") {
      // Combined view — merge all entries by timestamp
      const dateMap = {};
      historyData.forEach((account) => {
        const shortName = account.account_name.split(" ").slice(-1)[0] || account.account_name;
        account.entries.forEach((entry) => {
          const ts = entry.recorded_at;
          if (!dateMap[ts]) {
            dateMap[ts] = { date: ts };
          }
          dateMap[ts][shortName] = entry.balance;
        });
      });

      const sorted = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));

      // Forward-fill missing values
      const accountShortNames = historyData.map(a => a.account_name.split(" ").slice(-1)[0] || a.account_name);
      let lastValues = {};
      const filled = sorted.map(point => {
        const result = { ...point };
        accountShortNames.forEach(name => {
          if (result[name] !== undefined) {
            lastValues[name] = result[name];
          } else {
            result[name] = lastValues[name] || 0;
          }
        });
        result.Total = accountShortNames.reduce((sum, name) => sum + (result[name] || 0), 0);
        return result;
      });

      // Format date labels — use short date, add time if same day
      const dates = filled.map(p => p.date.split("T")[0]);
      const allSameDay = dates.length > 1 && dates.every(d => d === dates[0]);
      return filled.map(p => ({
        ...p,
        label: allSameDay
          ? new Date(p.date).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
          : new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      }));
    } else {
      // Single account view
      const account = historyData.find(a => a.account_id === selectedAccount);
      if (!account) return [];
      const entries = account.entries;
      const dates = entries.map(e => e.recorded_at.split("T")[0]);
      const allSameDay = dates.length > 1 && dates.every(d => d === dates[0]);
      return entries.map(entry => ({
        date: entry.recorded_at,
        label: allSameDay
          ? new Date(entry.recorded_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
          : new Date(entry.recorded_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        balance: entry.balance,
        change_type: entry.change_type,
      }));
    }
  };

  const chartData = buildChartData();
  const accountNames = historyData.map(a => a.account_name.split(" ").slice(-1)[0] || a.account_name);
  const totalBalance = historyData.reduce((sum, a) => sum + (a.current_balance || 0), 0);

  // Calculate overall change
  const getChange = () => {
    if (chartData.length < 2) return { amount: 0, percent: 0 };
    if (selectedAccount === "all") {
      const first = chartData[0].Total || 0;
      const last = chartData[chartData.length - 1].Total || 0;
      const change = last - first;
      const pct = first > 0 ? ((change / first) * 100).toFixed(1) : 0;
      return { amount: change, percent: pct };
    } else {
      const first = chartData[0].balance || 0;
      const last = chartData[chartData.length - 1].balance || 0;
      const change = last - first;
      const pct = first > 0 ? ((change / first) * 100).toFixed(1) : 0;
      return { amount: change, percent: pct };
    }
  };

  const change = getChange();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-stone-500">Loading balance history...</p>
        </CardContent>
      </Card>
    );
  }

  if (!historyData.length) {
    return (
      <Card data-testid="balance-history-empty">
        <CardContent className="py-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-[#1e3a5f]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <h3 className="font-semibold text-stone-900 mb-2">No Balance History Yet</h3>
          <p className="text-stone-500 text-sm mb-4">Take a snapshot to start tracking your account balances over time.</p>
          <Button onClick={handleSnapshot} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="take-first-snapshot-btn">
            Take First Snapshot
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="balance-history-section">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select value={selectedAccount} onValueChange={setSelectedAccount}>
            <SelectTrigger className="w-[220px]" data-testid="balance-history-account-select">
              <SelectValue placeholder="Select account..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accounts (Combined)</SelectItem>
              {historyData.map((account) => (
                <SelectItem key={account.account_id} value={account.account_id}>
                  {account.account_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {change.amount !== 0 && (
            <Badge
              variant="outline"
              className={`${change.amount >= 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-red-700 bg-red-50 border-red-200'}`}
              data-testid="balance-change-badge"
            >
              {change.amount >= 0 ? '+' : ''}{fmt(change.amount)} ({change.percent}%)
            </Badge>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleSnapshot}
          disabled={snapshotting}
          data-testid="record-snapshot-btn"
        >
          {snapshotting ? "Recording..." : "Record Snapshot"}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-[#1e3a5f]">
          <CardContent className="pt-3 pb-3 px-4">
            <p className="text-xs text-stone-500 mb-0.5">Total Balance</p>
            <p className="text-lg font-bold text-[#1e3a5f]" data-testid="total-balance">{fmt(totalBalance)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-3 pb-3 px-4">
            <p className="text-xs text-stone-500 mb-0.5">Accounts Tracked</p>
            <p className="text-lg font-bold text-violet-600">{historyData.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-3 pb-3 px-4">
            <p className="text-xs text-stone-500 mb-0.5">Data Points</p>
            <p className="text-lg font-bold text-amber-600">{historyData.reduce((s, a) => s + a.entries.length, 0)}</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${change.amount >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
          <CardContent className="pt-3 pb-3 px-4">
            <p className="text-xs text-stone-500 mb-0.5">Net Change</p>
            <p className={`text-lg font-bold ${change.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {change.amount >= 0 ? '+' : ''}{fmt(change.amount)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {selectedAccount === "all" ? "Combined Balance Over Time" : `${historyData.find(a => a.account_id === selectedAccount)?.account_name || "Account"} History`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length < 2 ? (
            <div className="py-12 text-center">
              <p className="text-stone-500 text-sm">Need at least 2 data points to chart. Update balances or record another snapshot to see trends.</p>
            </div>
          ) : (
            <div className="h-64 -ml-2" data-testid="balance-chart">
              <ResponsiveContainer width="100%" height="100%">
                {selectedAccount === "all" ? (
                  <AreaChart data={chartData}>
                    <defs>
                      {accountNames.map((name, i) => (
                        <linearGradient key={name} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={ACCOUNT_COLORS[i % ACCOUNT_COLORS.length]} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={ACCOUNT_COLORS[i % ACCOUNT_COLORS.length]} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={48} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                      formatter={(v, name) => [fmtFull(v), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {accountNames.map((name, i) => (
                      <Area
                        key={name}
                        type="monotone"
                        dataKey={name}
                        name={name}
                        stroke={ACCOUNT_COLORS[i % ACCOUNT_COLORS.length]}
                        fill={`url(#grad-${i})`}
                        strokeWidth={2}
                        stackId="1"
                      />
                    ))}
                  </AreaChart>
                ) : (
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="gradSingle" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1e3a5f" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#1e3a5f" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} width={48} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                      formatter={(v, name) => [fmtFull(v), name === "balance" ? "Balance" : name]}
                      labelFormatter={(l) => `Date: ${l}`}
                    />
                    <Area type="monotone" dataKey="balance" name="Balance" stroke="#1e3a5f" fill="url(#gradSingle)" strokeWidth={2} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual Account Cards */}
      {selectedAccount === "all" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {historyData.map((account, i) => {
            const entries = account.entries;
            const lastEntry = entries[entries.length - 1];
            const firstEntry = entries[0];
            const acctChange = entries.length >= 2 ? (lastEntry.balance - firstEntry.balance) : 0;
            return (
              <Card
                key={account.account_id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedAccount(account.account_id)}
                data-testid={`account-history-card-${account.account_id}`}
              >
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-3 h-10 rounded-full" style={{ backgroundColor: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length] }} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-stone-900 truncate">{account.account_name}</p>
                      <p className="text-[11px] text-stone-400">{account.bank_name} · {entries.length} records</p>
                    </div>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Current</p>
                      <p className="text-xl font-bold text-[#1e3a5f]">{fmt(account.current_balance)}</p>
                    </div>
                    {acctChange !== 0 && (
                      <Badge variant="outline" className={`text-xs ${acctChange >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}`}>
                        {acctChange >= 0 ? '+' : ''}{fmt(acctChange)}
                      </Badge>
                    )}
                  </div>
                  {lastEntry && (
                    <p className="text-[10px] text-stone-400 mt-2">
                      Last: {CHANGE_LABELS[lastEntry.change_type] || lastEntry.change_type} · {new Date(lastEntry.recorded_at).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Single Account Activity Log */}
      {selectedAccount !== "all" && (() => {
        const account = historyData.find(a => a.account_id === selectedAccount);
        if (!account) return null;
        return (
          <Card data-testid="account-activity-log">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Activity Log — {account.account_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[...account.entries].reverse().map((entry, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-stone-50/70">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        entry.change_type.includes('in') || entry.change_type === 'account_created'
                          ? 'bg-emerald-100 text-emerald-700'
                          : entry.change_type.includes('out')
                            ? 'bg-red-100 text-red-700'
                            : 'bg-[#1e3a5f]/10 text-[#1e3a5f]'
                      }`}>
                        {entry.change_type.includes('in') || entry.change_type === 'account_created' ? '+' : entry.change_type.includes('out') ? '-' : 'S'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-900">{CHANGE_LABELS[entry.change_type] || entry.change_type}</p>
                        <p className="text-[11px] text-stone-400">{new Date(entry.recorded_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#1e3a5f]">{fmtFull(entry.balance)}</p>
                      {entry.previous_balance !== undefined && entry.previous_balance !== entry.balance && (
                        <p className={`text-[11px] ${entry.balance >= entry.previous_balance ? 'text-emerald-600' : 'text-red-600'}`}>
                          {entry.balance >= entry.previous_balance ? '+' : ''}{fmtFull(entry.balance - entry.previous_balance)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};

export default BalanceHistory;
